import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Badge,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  Navbar,
  UncontrolledDropdown,
} from "reactstrap";
import { ME_USER_EDIT_FORM } from "../../../constants/modal.constant";
import { useGlobalCore } from "../../../cores/globals";
import { useMeCore } from "../../../cores/me";
import { useNotificationCore } from "../../../cores/notifications";
import { useSessionCore } from "../../../cores/session";
import { useIntl } from "react-intl";
import { Context } from "../../../LocaleWrapper";
import Avatar from "../Avatar";
import { EditMeUserForm } from "../editMeUser/EditMeUserForm";
import { UserNavbarMenu } from "./UserNavbarMenu";
import NotificationContainer from "../notifications/NotificationContainer";
import { PanelWembii } from "../PanelWembii";
import { CompanySelector } from "./CompanySelector";
import { PlanType } from "../../../cores/plan/contants/planType.constant";
import { UpgradePlanButton } from "../plan/UpgradePlanButton";
import { getFirstLetters } from "../../utils/stringUtils";
import { useRouterCore } from "../../../cores/router";
import { NotificationType } from "../../../cores/notifications/constants/notificationType.constant";
import { getNotificationTimestampMs } from "../../utils/notificationDisplay";
import cn from "classnames";
import { State } from "../../../redux/state";
import "./NotificationsDropdown.scss";

export default function NavBar() {
  const intl = useIntl();
  const { user: userSession, logout } = useSessionCore();
  const {
    reset,
    turnOnModal,
    setOpenSideBar,
    openSideBar,
    isDarkMode,
    setDarkMode,
  } = useGlobalCore();
  const {
    unreadNotifications,
    notifications,
    putReadNotifications,
  } = useNotificationCore();

  const { userPlan, companyRole, user, company, postMeLanguage } = useMeCore();

  const dismissedIds = useSelector((s: State) =>
    company?.id
      ? s.notification?.dismissedIdsByCompany?.[company.id] ?? []
      : []
  );
  const dismissedSet = useMemo(
    () => new Set(dismissedIds),
    [dismissedIds]
  );

  /** No leídas + publicaciones recientes (aunque el backend las marque leídas). */
  const dropdownNotifications = useMemo(() => {
    const unread = unreadNotifications.filter((n) => !dismissedSet.has(n.id));
    const notif = notifications.filter((n) => !dismissedSet.has(n.id));
    const unreadIds = new Set(unread.map((n) => n.id));
    const socialRead = notif.filter(
      (n) =>
        n.type === NotificationType.SOCIAL_POST && !unreadIds.has(n.id)
    );
    const merged = [...unread, ...socialRead];
    merged.sort(
      (a, b) => getNotificationTimestampMs(b) - getNotificationTimestampMs(a)
    );
    return merged.slice(0, 15);
  }, [unreadNotifications, notifications, dismissedSet]);

  const visibleUnreadCount = useMemo(
    () =>
      unreadNotifications.filter((n) => !dismissedSet.has(n.id)).length,
    [unreadNotifications, dismissedSet]
  );

  const { goToPlans, goToNotifications } = useRouterCore();
  const toggleSidebar = () => setOpenSideBar(!openSideBar);
  const toggleDarkMode = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDarkMode(!isDarkMode);
    },
    [isDarkMode, setDarkMode]
  );

  const darkModeToggleButton = (extraClassName = "mx-2") => (
    <button
      type="button"
      data-wembii-dark-mode-toggle=""
      className={`navbar-header-icon pointer d-inline-flex align-items-center justify-content-center ${extraClassName}`}
      onClick={toggleDarkMode}
      aria-label={intl.formatMessage({
        id: isDarkMode
          ? "app.navbar.switchToLightMode"
          : "app.navbar.switchToDarkMode",
      })}
    >
      <FontAwesomeIcon
        icon={isDarkMode ? ["fas", "sun"] : ["fas", "moon"]}
        color={isDarkMode ? "#E6EDF3" : "#6c757d"}
      />
    </button>
  );

  const onLogout = useCallback(() => {
    reset();
    logout();
  }, [logout, reset]);

  const onEditUser = useCallback(() => {
    turnOnModal(ME_USER_EDIT_FORM, <EditMeUserForm />);
  }, [turnOnModal]);

  const onEditIntegrations = useCallback(() => {
    turnOnModal(ME_USER_EDIT_FORM, <EditMeUserForm tab={"3"} />);
  }, [turnOnModal]);

  const onRemoveNotifications = useCallback(() => {
    putReadNotifications(company?.id!);
  }, [putReadNotifications, company]);

  const { locale, selectLanguage } = useContext(Context);

  const onSelectLanguage = useCallback(
    async (language: string) => {
      await postMeLanguage?.(language);
      selectLanguage(language);
    },
    [postMeLanguage, selectLanguage]
  );

  return (
    <PanelWembii
      className="wembii-navbar"
      backgroundColor={isDarkMode ? "#0B0E14" : undefined}
    >
      <div className="d-flex d-lg-none flex-column w-100 p-2 pb-3">
        <div className="d-flex align-items-center justify-content-between w-100">
          <button
            type="button"
            className="pointer flex-shrink-0 border-0 rounded d-flex align-items-center justify-content-center p-0"
            style={{
              height: 48,
              width: 48,
              minHeight: 48,
              minWidth: 48,
              backgroundColor: isDarkMode ? "#161B22" : "white",
              border: isDarkMode ? "1px solid #30363D" : "none",
            }}
            onClick={toggleSidebar}
            aria-label={openSideBar ? "Cerrar menú" : "Abrir menú"}
          >
            {!openSideBar ? (
              <FontAwesomeIcon
                icon={["fas", "bars"]}
                className="text-secondary"
                size="2x"
              />
            ) : (
              <FontAwesomeIcon
                icon={["fas", "times"]}
                className="text-secondary"
                size="2x"
              />
            )}
          </button>
          {darkModeToggleButton("flex-shrink-0")}
        </div>
        <div className="w-100 mt-2 company-selector-mobile-wrap">
          <CompanySelector layout="mobile" />
        </div>
      </div>

      <Navbar light expand="lg" className="d-none d-lg-flex container-fluid">
        <CompanySelector />
        <Container fluid className="d-flex justify-content-end">
          {company &&
            companyRole?.owner &&
            (userPlan?.name === PlanType.FREE || !!userSession?.freeTrial) && (
              <Nav className="mr-auto mx-2 " navbar>
                <UpgradePlanButton />
              </Nav>
            )}

          <Nav className="ml-auto align-items-center" navbar>
            {!!userSession?.freeTrial && (
              <h6 className="mx-2 my-0 p-0">
                {userSession.freeTrialDays -
                  moment().diff(moment(userSession.freeTrialDate), "days")}{" "}
                días restantes
              </h6>
            )}
            {darkModeToggleButton()}
            <UncontrolledDropdown nav inNavbar>
              <DropdownToggle nav className="p-0 position-relative">
                <Avatar
                  className="navbar-icon-pill pointer"
                  backgroundColor={isDarkMode ? "#161B22" : undefined}
                  color={isDarkMode ? "#E6EDF3" : undefined}
                  icon={
                    <FontAwesomeIcon
                      icon={["fas", "bell"]}
                      size="1x"
                      color={isDarkMode ? "#E6EDF3" : "grey"}
                      className="pointer pos"
                    />
                  }
                />
                {visibleUnreadCount > 0 && (
                  <Badge
                    color="danger"
                    className="text-light position-absolute float-right-corner"
                    pill
                  >
                    {visibleUnreadCount}
                  </Badge>
                )}
              </DropdownToggle>
              <DropdownMenu
                right
                className={cn(
                  "user-navbar-menu notifications-dropdown border-0 shadow py-0",
                  isDarkMode && "user-navbar-menu--dark"
                )}
              >
                <div className="user-navbar-menu__header notifications-dropdown__header">
                  <div
                    className="notifications-dropdown__header-icon"
                    aria-hidden
                  >
                    <FontAwesomeIcon icon={["fas", "bell"]} />
                  </div>
                  <div className="user-navbar-menu__meta min-width-0">
                    <div className="user-navbar-menu__name text-truncate">
                      {intl.formatMessage({
                        id: "app.navbar.notificationsMenuTitle",
                      })}
                    </div>
                    <div className="user-navbar-menu__email text-truncate">
                      {intl.formatMessage({
                        id: "app.notifications.pageSubtitle",
                      })}
                    </div>
                  </div>
                </div>

                <DropdownItem
                  divider
                  className="user-navbar-menu__divider my-0"
                />

                <DropdownItem
                  className="user-navbar-menu__item"
                  onClick={goToNotifications}
                >
                  <span className="user-navbar-menu__icon" aria-hidden>
                    <FontAwesomeIcon icon={["fas", "arrow-right"]} />
                  </span>
                  <span>
                    {intl.formatMessage({ id: "app.navbar.seeAll" })}
                  </span>
                </DropdownItem>

                <DropdownItem
                  divider
                  className="user-navbar-menu__divider my-0"
                />

                <div className="notifications-dropdown__list">
                  {dropdownNotifications.length > 0 ? (
                    dropdownNotifications.map((notification) => (
                      <NotificationContainer
                        unread={notification.status === 0}
                        key={notification.id}
                        notification={notification}
                        variant="dropdown"
                      />
                    ))
                  ) : (
                    <div className="notifications-dropdown__empty">
                      {intl.formatMessage({ id: "app.navbar.notNotification" })}
                    </div>
                  )}
                </div>

                {visibleUnreadCount > 0 ? (
                  <>
                    <DropdownItem
                      divider
                      className="user-navbar-menu__divider my-0"
                    />
                    <button
                      type="button"
                      className="notifications-dropdown__mark-all-read"
                      onClick={onRemoveNotifications}
                    >
                      <FontAwesomeIcon
                        icon={["fas", "check-double"]}
                        className="notifications-dropdown__mark-all-read-icon"
                        aria-hidden
                      />
                      <span>
                        {intl.formatMessage({
                          id: "app.navbar.readAllNotifications",
                        })}
                      </span>
                    </button>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>

            <UncontrolledDropdown nav inNavbar>
              <DropdownToggle nav className="p-0">
                <Avatar
                  className="navbar-icon-pill navbar-avatar-pill pointer mx-2"
                  backgroundColor={isDarkMode ? "#161B22" : undefined}
                  color={isDarkMode ? "#E6EDF3" : undefined}
                  label={getFirstLetters(
                    `${user?.lastName} ${user?.firstName}`
                  )}
                  image={user?.avatarUrl}
                />
              </DropdownToggle>
              <UserNavbarMenu
                user={user}
                isDarkMode={isDarkMode}
                userPlan={userPlan}
                locale={locale}
                onLogout={onLogout}
                onEditUser={onEditUser}
                onEditIntegrations={onEditIntegrations}
                goToPlans={goToPlans}
                onSelectLanguage={(language) => onSelectLanguage(language)}
              />
            </UncontrolledDropdown>
          </Nav>
        </Container>
      </Navbar>
    </PanelWembii>
  );
}
